import { useMemo, useState } from "react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent } from "@/components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Checkbox } from "@/components/ui/checkbox";
import { FileText, DollarSign, Mail, Edit, Trash2, Briefcase, X, Clock, FileSpreadsheet } from "lucide-react";
import { usePermissions } from "@/hooks/usePermissions";
import { SortableHead, useSortedRows } from "@/components/SortableHeader";
import { downloadCsv } from "@/utils/csv";

const EMPTY = { customer_id: "", technician_id: "", status: "open", search: "", start_date: "", end_date: "" };

const ACCESSORS = {
  date: (e) => e.date,
  customer_name: (e) => e.customer_name,
  technician_name: (e) => e.technician_name || "",
  description: (e) => e.description,
  time_spent: (e) => e.time_spent,
  status: (e) => e.status,
};

const hrs = (n) => `${(n || 0).toFixed(1)} h`;

const applyFilters = (entries, f) =>
  entries.filter((e) => {
    if (f.customer_id && e.customer_id !== f.customer_id) return false;
    if (f.technician_id && e.technician_id !== f.technician_id) return false;
    if (f.status === "open" && e.status === "closed") return false;
    if (f.status === "closed" && e.status !== "closed") return false;
    if (f.search && !`${e.description} ${e.customer_name} ${e.technician_name || ""}`.toLowerCase().includes(f.search.toLowerCase())) return false;
    if (f.start_date && e.date < f.start_date) return false;
    if (f.end_date && e.date > f.end_date) return false;
    return true;
  });

const summarize = (entries) => {
  const map = {};
  entries.forEach((e) => {
    const r = (map[e.customer_id] ||= { name: e.customer_name, hours: 0, count: 0, open: 0, closed: 0 });
    r.hours += e.time_spent || 0;
    r.count += 1;
    e.status === "closed" ? (r.closed += e.time_spent || 0) : (r.open += e.time_spent || 0);
  });
  return Object.values(map).sort((a, b) => b.hours - a.hours);
};

const today = () => new Date().toISOString().slice(0, 10);

const HoursByCustomer = ({ entries }) => {
  const rows = useMemo(() => summarize(entries), [entries]);
  const total = rows.reduce((s, r) => s + r.hours, 0);
  const exportSummary = () =>
    downloadCsv(`hours-by-customer-${today()}.csv`, ["Customer", "Entries", "Open Hours", "Closed/Invoiced Hours", "Total Hours"],
      [...rows.map((r) => [r.name, r.count, r.open.toFixed(2), r.closed.toFixed(2), r.hours.toFixed(2)]), ["TOTAL", entries.length, "", "", total.toFixed(2)]]);
  return (
    <Card className="bg-white border-0 shadow-lg" data-testid="hours-by-customer">
      <CardContent className="p-4">
        <div className="flex flex-wrap items-center justify-between gap-2 mb-3">
          <p className="font-semibold text-slate-800 flex items-center gap-2"><Clock className="w-4 h-4 text-blue-600" /> Hours by Customer</p>
          <div className="flex items-center gap-3">
            <p className="text-sm text-slate-500">Total <span className="font-bold text-blue-600" data-testid="hours-total">{hrs(total)}</span> · {entries.length} entries</p>
            <Button size="sm" variant="outline" onClick={exportSummary} disabled={rows.length === 0} data-testid="export-hours-summary-button"><FileSpreadsheet className="w-4 h-4 mr-1" /> Export Summary</Button>
          </div>
        </div>
        {rows.length === 0 ? (
          <p className="text-sm text-slate-400">No entries match the current filters</p>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-2">
            {rows.map((r) => (
              <div key={r.name} className="flex items-center justify-between rounded-lg bg-slate-50 px-3 py-2" data-testid={`hours-customer-${r.name}`}>
                <div className="min-w-0">
                  <p className="text-sm font-medium text-slate-800 truncate">{r.name}</p>
                  <p className="text-xs text-slate-500">{r.count} entr{r.count === 1 ? "y" : "ies"}</p>
                </div>
                <p className="font-bold text-blue-600 whitespace-nowrap">{hrs(r.hours)}</p>
              </div>
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
};

const Action = ({ onClick, className, title, testId, children }) => (
  <Button size="sm" variant="ghost" onClick={onClick} className={`h-8 px-2 ${className}`} title={title} data-testid={testId}>{children}</Button>
);

export const WorkEntryTable = ({ entries, customers, technicians, actions, currentUser }) => {
  const { can } = usePermissions();
  const [filters, setFilters] = useState(EMPTY);
  const [selectedIds, setSelectedIds] = useState(new Set());
  const set = (patch) => setFilters({ ...filters, ...patch });
  const filtered = useMemo(() => applyFilters(entries, filters), [entries, filters]);
  const { sorted, sort, toggle } = useSortedRows(filtered, ACCESSORS, "date", "desc");
  const active = Object.entries(filters).some(([k, v]) => (k === "status" ? v !== "open" : v));

  const selectable = filtered.filter((e) => e.status !== "closed");
  const selected = selectable.filter((e) => selectedIds.has(e.id));
  const sameCustomer = new Set(selected.map((e) => e.customer_id)).size === 1;
  const selectedHours = selected.reduce((s, e) => s + (e.time_spent || 0), 0);
  const toggleOne = (id) => setSelectedIds((prev) => { const n = new Set(prev); n.has(id) ? n.delete(id) : n.add(id); return n; });
  const toggleAll = (checked) => setSelectedIds(checked ? new Set(selectable.map((e) => e.id)) : new Set());
  const clearSelection = () => setSelectedIds(new Set());

  const exportEntries = () =>
    downloadCsv(`work-entries-${today()}.csv`, ["Date", "Customer", "Technician", "Description", "Hours", "Status"],
      sorted.map((e) => [e.date, e.customer_name, e.technician_name || "", e.description, (e.time_spent || 0).toFixed(2), e.status || "open"]));

  return (
    <div className="space-y-4">
      <Card className="bg-white border-0 shadow-lg" data-testid="work-filters">
        <CardContent className="p-4 space-y-3">
          <div className="grid grid-cols-1 md:grid-cols-3 xl:grid-cols-6 gap-4">
            <div>
              <Label>Customer</Label>
              <Select value={filters.customer_id || "all"} onValueChange={(v) => set({ customer_id: v === "all" ? "" : v })}>
                <SelectTrigger data-testid="work-filter-customer"><SelectValue placeholder="All Customers" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Customers</SelectItem>
                  {customers.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Technician</Label>
              <Select value={filters.technician_id || "all"} onValueChange={(v) => set({ technician_id: v === "all" ? "" : v })}>
                <SelectTrigger data-testid="work-filter-technician"><SelectValue placeholder="All Technicians" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Technicians</SelectItem>
                  {technicians.map((t) => <SelectItem key={t.id} value={t.id}>{t.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Status</Label>
              <Select value={filters.status} onValueChange={(v) => set({ status: v })}>
                <SelectTrigger data-testid="work-filter-status"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="open">Open</SelectItem>
                  <SelectItem value="closed">Closed</SelectItem>
                  <SelectItem value="all">All</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Search</Label>
              <Input value={filters.search} onChange={(e) => set({ search: e.target.value })} placeholder="Description, customer, tech…" data-testid="work-filter-search" />
            </div>
            <div>
              <Label>From</Label>
              <Input type="date" value={filters.start_date} onChange={(e) => set({ start_date: e.target.value })} data-testid="work-filter-start" />
            </div>
            <div>
              <Label>To</Label>
              <Input type="date" value={filters.end_date} onChange={(e) => set({ end_date: e.target.value })} data-testid="work-filter-end" />
            </div>
          </div>
          <div className="flex items-center justify-between">
            <p className="text-sm text-slate-500" data-testid="work-result-count">Showing {filtered.length} of {entries.length} work entries</p>
            {active && <Button variant="ghost" size="sm" onClick={() => setFilters(EMPTY)} data-testid="work-filters-clear"><X className="w-4 h-4 mr-1" /> Clear filters</Button>}
          </div>
        </CardContent>
      </Card>

      <HoursByCustomer entries={filtered} />

      {selected.length > 0 && can("work.convert") && (
        <div className="flex flex-wrap items-center gap-3 p-3 rounded-lg bg-purple-50 border border-purple-200" data-testid="work-bulk-bar">
          <p className="text-sm font-medium text-purple-900" data-testid="work-bulk-count">{selected.length} open entr{selected.length === 1 ? "y" : "ies"} · {hrs(selectedHours)}{sameCustomer ? ` · ${selected[0].customer_name}` : ""}</p>
          {!sameCustomer && <p className="text-sm text-red-600" data-testid="work-bulk-mixed">Select entries from a single customer to invoice them together</p>}
          <Button size="sm" onClick={() => actions.bulkConvert(selected, clearSelection)} disabled={!sameCustomer} className="bg-purple-600 hover:bg-purple-700" data-testid="work-bulk-convert-button">
            <DollarSign className="w-4 h-4 mr-1" /> Convert to One Invoice
          </Button>
          <Button size="sm" variant="ghost" onClick={clearSelection} data-testid="work-bulk-clear"><X className="w-4 h-4 mr-1" /> Clear</Button>
        </div>
      )}

      <Card className="bg-white border-0 shadow-lg" data-testid="work-table-card">
        <CardContent className="p-0">
          <div className="flex items-center justify-between px-4 py-2 border-b border-slate-100">
            <p className="text-sm text-slate-500">{sorted.length} row{sorted.length === 1 ? "" : "s"} · sorted by {sort.key.replace("_", " ")} {sort.dir === "asc" ? "↑" : "↓"}</p>
            <Button size="sm" variant="outline" onClick={exportEntries} disabled={sorted.length === 0} data-testid="export-work-entries-button"><FileSpreadsheet className="w-4 h-4 mr-1" /> Export Entries CSV</Button>
          </div>
          <div className="overflow-x-auto">
          <Table data-testid="work-table">
            <TableHeader>
              <TableRow className="bg-slate-50">
                <TableHead className="w-10">
                  <Checkbox checked={selectable.length > 0 && selected.length === selectable.length ? true : selected.length > 0 ? "indeterminate" : false} onCheckedChange={(v) => toggleAll(!!v)} disabled={selectable.length === 0} aria-label="Select all open entries" data-testid="work-select-all" />
                </TableHead>
                <SortableHead label="Date" sortKey="date" sort={sort} onToggle={toggle} />
                <SortableHead label="Customer" sortKey="customer_name" sort={sort} onToggle={toggle} />
                <SortableHead label="Technician" sortKey="technician_name" sort={sort} onToggle={toggle} />
                <SortableHead label="Description" sortKey="description" sort={sort} onToggle={toggle} />
                <SortableHead label="Hours" sortKey="time_spent" sort={sort} onToggle={toggle} className="text-right" align="right" />
                <SortableHead label="Status" sortKey="status" sort={sort} onToggle={toggle} />
                <TableHead className="text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {sorted.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={8} className="py-16 text-center" data-testid="work-table-empty">
                    <Briefcase className="w-12 h-12 text-slate-300 mx-auto mb-3" />
                    <p className="text-slate-600 font-medium">No work entries match the current filters</p>
                  </TableCell>
                </TableRow>
              ) : (
                sorted.map((entry) => (
                  <TableRow key={entry.id} className={`hover:bg-slate-50 ${selectedIds.has(entry.id) && entry.status !== "closed" ? "bg-purple-50/60" : ""}`} data-testid="work-entry-row">
                    <TableCell>
                      {entry.status !== "closed" && <Checkbox checked={selectedIds.has(entry.id)} onCheckedChange={() => toggleOne(entry.id)} aria-label="Select entry" data-testid="work-entry-select" />}
                    </TableCell>
                    <TableCell className="whitespace-nowrap text-slate-600">{entry.date}</TableCell>
                    <TableCell className="font-semibold text-slate-800">{entry.customer_name}</TableCell>
                    <TableCell className="text-slate-600">{entry.technician_name || <span className="text-slate-400">—</span>}</TableCell>
                    <TableCell className="text-slate-700 max-w-md"><p className="line-clamp-2" title={entry.description}>{entry.description}</p></TableCell>
                    <TableCell className="text-right font-bold text-blue-600 whitespace-nowrap" data-testid="entry-time-spent">{hrs(entry.time_spent)}</TableCell>
                    <TableCell>
                      <Badge className={entry.status === "closed" ? "bg-green-100 text-green-700" : "bg-orange-100 text-orange-700"}>{(entry.status || "open").toUpperCase()}</Badge>
                    </TableCell>
                    <TableCell className="text-right whitespace-nowrap">
                      <Action onClick={() => actions.downloadPDF(entry.id)} className="text-green-600 hover:bg-green-50" title="Download work order PDF" testId="download-work-order-button"><FileText className="w-4 h-4" /></Action>
                      {entry.status !== "closed" && can("work.convert") && (
                        <Action onClick={() => actions.convertToInvoice(entry.id)} className="text-purple-600 hover:bg-purple-50" title="Convert to invoice" testId="convert-to-invoice-button"><DollarSign className="w-4 h-4" /></Action>
                      )}
                      {can("work.edit") && <Action onClick={() => actions.emailWorkOrder(entry.id)} className="text-orange-600 hover:bg-orange-50" title="Email work order" testId="email-work-order-button"><Mail className="w-4 h-4" /></Action>}
                      {can("work.edit") && (entry.status !== "closed" || can("work.view_all")) && (
                        <Action onClick={() => actions.onEdit(entry)} className="text-blue-600 hover:bg-blue-50" title="Edit" testId="edit-work-entry-button"><Edit className="w-4 h-4" /></Action>
                      )}
                      {can("work.delete") && <Action onClick={() => actions.onDelete(entry.id)} className="text-red-600 hover:bg-red-50" title="Delete" testId="delete-work-entry-button"><Trash2 className="w-4 h-4" /></Action>}
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};
